import { useEffect, useMemo, useState } from "react";

import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { BudgetMixProvider } from "@features/mmm/budget-mix-provider";
import {
  useMMMBudgetMixChartMode,
  useMMMBudgetMixKpiId,
  useMMMDateRange,
} from "@features/mmm/hooks/mmm-url-state";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import {
  type FetchHistoricalMixForCycle,
  type FetchHistoricalMixForCycleVariables,
} from "@north-beam/nb-client-side";
import { MMMClientState } from "@north-beam/nb-common";
import { CreateCustomBudgetTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { HISTORICAL_MIXES_FOR_CYCLE_QUERY } from "@pages/mmm/explore-budgets-v2/explore-budgets-queries";
import { useBudgetMixes } from "@pages/mmm/hooks/use-budget-mixes";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";
import { useMMM } from "@pages/mmm/mmm-provider";

import BudgetMixCardContainer from "../budget-mix-card-container";
import { useMMMAdminMode } from "../budget-mix-table/utils";
import { useSelectedCyclePriorPeriod } from "../hooks/use-selected-cycle-prior-period";
import { AddSpendDataModal } from "../scenario-modeling/add-spend-data-modal";

import { MixesControls } from "./mixes-controls";

export const MixesSection = () => {
  const { selectedCycle } = useSelectedCycle();

  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();
  const { validateOrResetBudgetMixKpi } = useMMMBudgetMixKpiId();
  const { isBudgetMixDataLoading } = useMMM();

  const [cardMode, setCardMode] = useMMMBudgetMixChartMode();
  const [isAddSpendDataModalOpen, setIsAddSpendDataModalOpen] = useState(false);
  const openAddSpendDataModal = () => setIsAddSpendDataModalOpen(true);
  const trackMMMEvent = useTrackMMMEvent(MMMTab.CREATE_CUSTOM_BUDGETS);
  const { kpis } = useClientKpis();

  const [historicals, setHistoricals] =
    useState<FetchHistoricalMixForCycle | null>(null);
  const [dateRange, setDateRange] = useMMMDateRange();

  const isAdminMode = useMMMAdminMode(user);
  const { getSelectedCyclePriorPeriod } = useSelectedCyclePriorPeriod();

  const dateRangeForHistoricalData = useMemo(() => {
    if (dateRange && dateRange.startDate && dateRange.endDate) {
      return dateRange;
    }
    const priorPeriod = getSelectedCyclePriorPeriod();
    setDateRange(priorPeriod);
    return priorPeriod;
  }, [dateRange, setDateRange, getSelectedCyclePriorPeriod]);

  const [budgetMixes] = useBudgetMixes();

  const { data: historicalMixData, loading: historicalMixDataLoading } =
    useNorthbeamQuery<
      FetchHistoricalMixForCycle,
      FetchHistoricalMixForCycleVariables
    >(HISTORICAL_MIXES_FOR_CYCLE_QUERY, {
      variables: {
        id: selectedCycle.id,
        dateRange: dateRangeForHistoricalData,
      },
    });

  useEffect(() => {
    validateOrResetBudgetMixKpi();
  }, [hasFeatureFlag, validateOrResetBudgetMixKpi]);

  useEffect(() => {
    if (historicalMixData) {
      setHistoricals(historicalMixData);
    }
  }, [historicalMixData]);

  const shouldShowBudgetMix = useMemo(() => {
    const budgetMixShowableStates = [
      MMMClientState.CREATING_SETTINGS,
      MMMClientState.ADOPTING_MIX,
      MMMClientState.TRACKING_MIX,
      MMMClientState.PREPARING_MIX,
      MMMClientState.REVIEWING_PERIOD,
    ];
    if (isAdminMode) {
      budgetMixShowableStates.push(MMMClientState.PREPARING_MIX);
    }
    return budgetMixShowableStates.includes(selectedCycle.state);
  }, [selectedCycle.state, isAdminMode]);

  if (shouldShowBudgetMix && !budgetMixes.length && isBudgetMixDataLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <LoadingSlide />
      </div>
    );
  }

  return (
    <>
      <MixesControls
        budgetMixes={budgetMixes}
        cardMode={cardMode}
        setCardMode={setCardMode}
        openAddSpendDataModal={() => setIsAddSpendDataModalOpen(true)}
      />
      <div className="mt-5 flex justify-start gap-4">
        <BudgetMixProvider>
          <BudgetMixCardContainer
            loadingHistoricals={historicalMixDataLoading || !dateRange}
            mode={cardMode}
            historical={historicals?.me?.mmm?.cycle?.historicalMix}
            data={budgetMixes}
            dateRange={dateRangeForHistoricalData}
            openAddSpendDataModal={openAddSpendDataModal}
          />
        </BudgetMixProvider>
      </div>
      {kpis.length > 0 && (
        <AddSpendDataModal
          isOpen={isAddSpendDataModalOpen}
          onClose={() => {
            trackMMMEvent({
              component: CreateCustomBudgetTrackedComponent.UPLOAD_DATA_MODAL,
              action: "onClose",
            });
            setIsAddSpendDataModalOpen(false);
          }}
        />
      )}
    </>
  );
};
